/**
 * Analytics Portal V1 — dashboard export control (Phase 18, plan §16).
 *
 * Sits in the header of the five single-entity dashboards and exports **the entity currently on
 * screen** with the range, comparison state and viewer timezone the dashboard itself is using.
 * The Summary dashboard never mounts it, and there is no portfolio, multi-entity, recurring,
 * shared-link or Named Visitor export anywhere in this file.
 *
 * Privacy and safety notes:
 *   - The server authorizes every request; this component only chooses which state to render from
 *     the capabilities the server already sent. PRO sees the CAPITAL upgrade state, Free never
 *     reaches this surface (the portal is locked earlier).
 *   - The PDF dialog sends the server's own section identifiers, never display labels, and
 *     Executive Summary cannot be cleared. The selection lives in component state only: nothing
 *     is written to localStorage, sessionStorage, IndexedDB or a cookie, and the downloaded file
 *     is handed to the browser and released immediately.
 *   - One generation at a time: the control is disabled while a request is in flight, so a double
 *     click, a double tap or Enter-repeat cannot spend a second export from the hourly budget.
 */
import React, { useState } from "react";
import { Download, FileSpreadsheet, FileText, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  FORCED_PDF_SECTIONS, PDF_SECTIONS, PDF_SECTION_KEYS, exportAnalytics,
  normalizeAnalyticsError,
} from "@/lib/analyticsApi";

export const ExportMenu = ({ entityType, entityId, capabilities, range, timezone }) => {
  const canPdf = Boolean(capabilities?.export_pdf);
  const canCsv = Boolean(capabilities?.export_csv);
  const [busy, setBusy] = useState(null);          // "pdf" | "csv" | null
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sections, setSections] = useState(PDF_SECTION_KEYS);

  if (!entityType || !entityId) return null;

  if (!canPdf && !canCsv) {
    return (
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground"
         data-testid="analytics-export-upsell">
        <Lock className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
        PDF and CSV exports are included with CAPITAL
      </p>
    );
  }

  const run = async (format, chosen) => {
    if (busy) return;
    setBusy(format);
    const pending = toast.loading(
      format === "pdf" ? "Building your PDF report…" : "Building your CSV package…");
    try {
      await exportAnalytics({ format, entityType, entityId, range, tz: timezone,
                              sections: chosen });
      toast.success(format === "pdf" ? "PDF report downloaded." : "CSV package downloaded.",
                    { id: pending });
      setDialogOpen(false);
    } catch (error) {
      const safe = normalizeAnalyticsError(error);
      toast.error(safe.message, { id: pending });
    } finally {
      setBusy(null);
    }
  };

  const toggle = (key) => setSections((current) => (
    current.includes(key) ? current.filter((item) => item !== key) : [...current, key]));

  return (
    <div className="flex items-center" data-testid="analytics-export">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary" size="sm" disabled={Boolean(busy)}
                  aria-label="Export this dashboard" data-testid="analytics-export-trigger">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
                  : <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />}
            {busy ? "Preparing…" : "Export"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {canPdf && (
            <DropdownMenuItem onSelect={() => { setSections(PDF_SECTION_KEYS); setDialogOpen(true); }}
                              data-testid="analytics-export-pdf">
              <FileText className="mr-2 h-4 w-4" aria-hidden="true" /> Download PDF
            </DropdownMenuItem>
          )}
          {canCsv && (
            <DropdownMenuItem onSelect={() => run("csv")}
                              data-testid="analytics-export-csv">
              <FileSpreadsheet className="mr-2 h-4 w-4" aria-hidden="true" /> Download CSV (ZIP)
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!busy) setDialogOpen(open); }}>
        <DialogContent className="sm:max-w-md" data-testid="analytics-export-pdf-dialog">
          <DialogHeader>
            <DialogTitle>PDF report sections</DialogTitle>
            <DialogDescription>
              Choose what to include. The report covers the entity and date range currently on
              screen, and never contains visitor identities or contact details.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2.5">
            {PDF_SECTIONS.map((section) => {
              const forced = FORCED_PDF_SECTIONS.includes(section.key);
              const checked = forced || sections.includes(section.key);
              return (
                <label key={section.key}
                       className="flex cursor-pointer items-center gap-2.5 rounded-lg px-1 py-1 text-sm text-foreground">
                  <Checkbox checked={checked} disabled={forced || Boolean(busy)}
                            onCheckedChange={() => toggle(section.key)}
                            aria-label={section.label}
                            data-testid={`analytics-export-section-${section.key}`} />
                  <span>{section.label}</span>
                  {forced && (
                    <span className="text-xs text-muted-foreground">Always included</span>
                  )}
                </label>
              );
            })}
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="secondary" size="sm" disabled={Boolean(busy)}
                    onClick={() => setSections(PDF_SECTION_KEYS)}
                    data-testid="analytics-export-select-all">Select all</Button>
            <Button variant="secondary" size="sm" disabled={Boolean(busy)}
                    onClick={() => setSections(FORCED_PDF_SECTIONS)}
                    data-testid="analytics-export-clear-optional">Clear optional</Button>
            <Button size="sm" disabled={Boolean(busy)} onClick={() => run("pdf", sections)}
                    data-testid="analytics-export-pdf-confirm">
              {busy === "pdf" && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />}
              {busy === "pdf" ? "Generating…" : "Download PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
